import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RestLink } from 'apollo-link-rest';
import TimeoutLink from 'apollo-link-timeout';

import { apiHost } from '@savvy/deprecated-shared/api/api-host';
import { LocalStorage } from '@savvy/deprecated-shared/utils/local-storage';
import { getSalesforceAccessToken, SALESFORCE_CLIENT_NAME } from '@savvy/deprecated-shared/utils/salesforce';

const timeoutLink = new TimeoutLink(30000);

const httpLink = createHttpLink({
  uri: `${apiHost}/graphql`,
  credentials: 'include',
});

const GENERAL_AUTH_SCHEME = 'Bearer';

const authLink = setContext(async (_, { headers }) => {
  // Get the authentication token from local storage if it exists
  const accessToken = await LocalStorage.getItem('accessToken');

  // Return the headers to the context so httpLink can read them
  let authHeader = null;
  if (accessToken) {
    authHeader = `${GENERAL_AUTH_SCHEME} ${accessToken}`;
  }

  return {
    headers: {
      ...headers,
      ...(authHeader && { authorization: authHeader }),
    },
  };
});

let onNetworkAuthError: (() => void) | null = null;

export const setOnNetworkAuthError = (handler: (() => void) | null) => {
  onNetworkAuthError = handler;
};

const CLIENT_OUTDATED_CODE = 'CLIENT_OUTDATED';

export type ClientOutdatedInfo = {
  readonly operationName: string;
};

let onClientOutdated: ((info: ClientOutdatedInfo) => void) | null = null;

/**
 * Register the handler invoked when Rails reports the client's query is no longer
 * valid against the live schema — `ClientOutdatedClassifier` re-tags schema-drift
 * validation errors with `extensions.code = 'CLIENT_OUTDATED'`. Sound as a
 * client-is-behind signal only because Rails always deploys before Vercel; see the
 * deploy-order invariant in `docs/plans/2026-07-23-version-skew-handling.md`.
 *
 * The version-skew refresh controller registers this; pass null to clear.
 */
export const setOnClientOutdated = (handler: ((info: ClientOutdatedInfo) => void) | null) => {
  onClientOutdated = handler;
};

function sfApiClientUrl() {
  if (process.env['NEXT_PUBLIC_VERCEL_ENV'] === 'development') {
    return 'https://savvywealthinc--delegate.sandbox.my.salesforce.com/services/data/v56.0/';
  }
  if (process.env['NEXT_PUBLIC_VERCEL_ENV'] === 'demo') {
    return 'https://savvywealthinc--demo.sandbox.my.salesforce.com/services/data/v56.0/';
  }
  return 'https://savvywealthinc.my.salesforce.com/services/data/v56.0/';
}
function sfRestLink() {
  return new RestLink({
    uri: sfApiClientUrl(),
    headers: {
      Authorization: `Bearer ${getSalesforceAccessToken()}`,
    },
    responseTransformer: (response: unknown) => {
      // @ts-expect-error response not typed
      if (typeof response.json !== 'function') {
        return {};
      }

      // @ts-expect-error response not typed
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      return response.json().then((data: unknown) => {
        // @ts-expect-error not typed
        if (data.records) {
          // @ts-expect-error not typed
          const records: unknown[] = data.records;
          // NextRecordsUrl is a URL to the next page of records for a paginated response
          // @ts-expect-error not typed
          if (data.nextRecordsUrl) {
            const lastRecord = records[records.length - 1];
            const updatedLastRecord = {
              // @ts-expect-error not typed
              ...lastRecord,
              // @ts-expect-error not typed
              nextRecordsUrl: data.nextRecordsUrl,
            };

            return [...records.slice(0, records.length - 1), updatedLastRecord];
          }
          return records;
          // @ts-expect-error not typed
        } else if (data.fields) {
          // @ts-expect-error not typed
          return data.fields;
        }
        return data;
      });
    },
  });
}

let apolloClient: ApolloClient<unknown> | null;
export const createApolloClient = () => {
  if (apolloClient) return apolloClient;
  const errorLink = onError(({ networkError, graphQLErrors, operation }) => {
    if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
      onNetworkAuthError?.();
    }
    // Schema drift arrives as a GraphQL validation error at HTTP 200, so it never
    // reaches the networkError branch above.
    if (graphQLErrors?.some((error) => error.extensions?.['code'] === CLIENT_OUTDATED_CODE)) {
      onClientOutdated?.({ operationName: operation.operationName });
    }
  });
  apolloClient = new ApolloClient({
    connectToDevTools: true,
    link: ApolloLink.from([
      errorLink,
      ApolloLink.split(
        (operation) => {
          return operation.getContext()['clientName'] === SALESFORCE_CLIENT_NAME; // Routes the query to the proper client
        },
        timeoutLink.concat(sfRestLink()),
        timeoutLink.concat(authLink.concat(httpLink)),
      ),
    ]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            alerts: {
              // Every arg that narrows the feed must be a key arg, or a filter change is a cache hit with no refetch; `after` stays out so the merge fn appends pages within one entry.
              keyArgs: [
                'userId',
                'alertTypes',
                'primaryAdvisorIds',
                'householdIds',
                'status',
                'since',
                'first',
                'query',
              ],
              merge(
                existing: { edges?: readonly unknown[]; pageInfo?: unknown; totalCount?: number } | undefined,
                incoming: { edges?: readonly unknown[]; pageInfo?: unknown; totalCount?: number },
                { args }: { args?: { after?: string | null } | null },
              ) {
                if (args?.after !== undefined && args.after !== null) {
                  return {
                    ...incoming,
                    edges: [...(existing?.edges ?? []), ...(incoming.edges ?? [])],
                  };
                }
                return incoming;
              },
            },
          },
        },
        SalesforceAccount: {
          keyFields: ['Id'],
          fields: {
            Contacts: {
              merge(existing, incoming, { mergeObjects }) {
                return mergeObjects(existing, incoming);
              },
            },
          },
        },
        SalesforceContact: {
          keyFields: ['Id'],
        },
      },
    }),
  });
  return apolloClient;
};
